import { useCallback, useState } from 'react';
import { sdk } from 'sdk';
import { ProductQuery } from 'shared/types/query/ProductQuery';
import { ProductPaginatedResult } from 'shared/types/result';
import useSWR from 'swr';

/**
 * Hook for product search functionality
 * @param query Product query parameters
 * @returns Product search results and methods
 */
export const useProductSearch = (query: ProductQuery) => {
  const {
    data: productsData,
    isLoading,
    mutate,
    isValidating,
  } = useSWR(
    ['/action/product/search', query.offset, query.categorySlug, query.activeFacets, query.sort],
    () => sdk.composableCommerce.search.productSearch(query),
    {
      keepPreviousData: true,
    },
  );
  const productsPaginatedResults = productsData?.isError
    ? {}
    : { data: productsData?.data as unknown as ProductPaginatedResult };

  return {
    isLoading,
    productsPaginatedResults,
    mutate,
    isValidating,
  };
};

/**
 * Hook for product text search functionality
 * @returns Text search methods and state
 */
export const useTextSearch = (searchInput: string, query: ProductQuery, identifier: string) => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(searchInput);

  const {
    data: response,
    isLoading,
    mutate,
  } = useSWR(
    searchTerm
      ? [
          '/action/search/textSearch',
          searchTerm,
          identifier,
          query?.offset,
          query?.sort,
          query?.activeFacets,
          query?.limit,
        ]
      : null,
    async () => {
      if (!searchTerm) return null;
      return sdk.composableCommerce.search.textSearch({ searchString: searchTerm }, query);
    },
    {
      keepPreviousData: true,
      dedupingInterval: 5000,
    },
  );

  const textSearchResults =
    response && !response.isError ? { data: response.data as unknown as ProductPaginatedResult } : { data: [] };

  /**
   * Search for text with the given string
   * @param term The search term
   * @returns The search results
   */
  const textSearch = useCallback(
    async (searchTerm: string) => {
      setSearchTerm(searchTerm);
      return mutate();
    },
    [mutate],
  );

  const clearTextSearch = useCallback(() => {
    setSearchTerm(undefined);
  }, []);

  return {
    textSearch,
    clearTextSearch,
    textSearchLoading: isLoading,
    textSearchResults,
    textSearchTerm: searchTerm,
  };
};
